import React from 'react';
import TranslationTooltip from '../translationTooltip/TranslationTooltip';
import './PassageWithTranslation.css';

interface PassageWithTranslationProps {
  original: string[];
  translations: string[];
  phonetics: string[] | null;
  savedWords: string[];
  showPhonetics: boolean;
  languageCode: string;
  align?: 'center' | 'left' | 'right';
  fontSize?: 'small' | 'large';
  canShowTooltip?: boolean;
  // Props passed down from ReadStoryPage
  activeTooltipId?: string | null;
  onOpen?: (tooltipId: string | null) => void;
  lineIndex?: number;
}

// Define the default function outside the component for stable reference
const defaultOnOpen = () => {};

const PassageWithTranslation: React.FC<PassageWithTranslationProps> = ({
  original,
  translations,
  phonetics,
  savedWords,
  showPhonetics,
  languageCode,
  align = 'left',
  fontSize = 'large',
  canShowTooltip = true,
  activeTooltipId = null,
  onOpen = defaultOnOpen, // Use the stable default function reference
  lineIndex = 0,
}) => {
  if (original.length !== translations.length) {
    throw new Error('Mismatch between original and translations lengths');
  }

  // Determine if the component is controlled based on whether onOpen is the default
  const isControlled = onOpen !== defaultOnOpen;

  // Restore gap calculation for flexbox
  const gap =
    languageCode.includes('zh') || languageCode.includes('ja') ? '0px' : '5px';

  // Helper function to render a single word/tooltip element
  const renderElement = (elIndex: number) => {
    const currentWord = original[elIndex];
    const currentTranslation = translations[elIndex];
    const currentPhonetic = phonetics ? phonetics[elIndex] : null;
    const tooltipId = `${lineIndex}-${elIndex}`; // Unique ID combining line and element index

    return currentTranslation ? (
      <TranslationTooltip
        key={tooltipId} // Use the unique ID as the key
        tooltipId={tooltipId}
        word={currentWord}
        translation={currentTranslation}
        phonetic={currentPhonetic}
        showPhonetics={showPhonetics}
        saved={savedWords.indexOf(currentWord) > -1}
        fontSize={fontSize}
        canShowTooltip={canShowTooltip}
        activeTooltipId={activeTooltipId} // Pass down active state
        onOpen={onOpen} // Pass down handler
        isControlled={isControlled} // Pass down control status
      >
        {currentWord}
      </TranslationTooltip>
    ) : (
      <span key={`${lineIndex}-${elIndex}`} className="passage-content">
        {/* Use index as key */}
        {phonetics && (
          <span
            style={{ visibility: showPhonetics ? 'visible' : 'hidden' }}
            className={`passage-phonetic ${fontSize}`}
          >
            {currentPhonetic}
          </span>
        )}
        <span className={`passage-word ${fontSize}`}>{currentWord}</span>
      </span>
    );
  };

  return (
    <div
      className="passage"
      style={{
        columnGap: gap, // Apply gap only horizontally
        justifyContent: align, // Restore flexbox alignment
        alignItems: align, // Restore flexbox alignment
        textAlign: align,
      }}
    >
      {original.length === 1
        ? // If only one element, render it directly
          renderElement(0)
        : // Otherwise, map and group the last two elements
          original.map((word, index) => {
            // If this is the second-to-last element, group it with the last one
            if (index === original.length - 2) {
              return (
                // This span acts as a single flex item containing the last two elements
                <span
                  key={`group-${lineIndex}-${index}`}
                  style={{ display: 'inline-flex', gap: gap }}
                >
                  {' '}
                  {/* Use inline-flex to apply gap internally */}
                  {renderElement(index)}
                  {renderElement(index + 1)}
                </span>
              );
            }

            // If this is the last element, it was already rendered as part of the group, so skip.
            if (index === original.length - 1) {
              return null;
            }

            // Otherwise (not the last or second-to-last), render the element normally
            return renderElement(index);
          })}
    </div>
  );
};

export default PassageWithTranslation;
